import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
// Add the interceptor token (one folder backward)
import { LoadingUIEnabled } from '../loading.interceptor';

@Injectable({ providedIn: 'root' })
export class ReferenceDisplayService {
    private http = inject(HttpClient);
    private backend = inject(backendEndPointToken);

    // Cache of ref -> display
    private cache: Record<string, string> = {};
    // Track in-flight requests to prevent "thundering herd" redundant calls
    private inFlightRequests = new Map<string, Observable<string>>();

    // Public stream for consumers that want to react to any ref display updates
    readonly refDisplays$ = new BehaviorSubject<Record<string, string>>({});

    // Ensure display is available (fetch if needed)
    ensure(ref: string, options?: { instantReplace?: boolean; keepReference?: boolean, displayPresent?: string | null }): Observable<string> {
        if (!ref) return of('');
        
        const cached = this.cache[ref];
        if (cached) return of(this.applyDisplayFormatting(ref, cached, options));

        // Check if a request is already in progress for this reference
        const inFlight = this.inFlightRequests.get(ref);
        if (inFlight) {
            return inFlight.pipe(
                map(display => this.applyDisplayFormatting(ref, display, options))
            );
        }

        const { type, id } = this.splitRef(ref);
        if (!type || !id) {
            this.updateCache(ref, ref);
            return of(this.applyDisplayFormatting(ref, ref, options));
        }

        if (options?.displayPresent && options.displayPresent?.trim()?.length > 0) {
            return of(options.displayPresent).pipe(
                tap((display) => this.updateCache(ref, display)),
                map((display) => this.applyDisplayFormatting(ref, display, options)),
                catchError(() => {
                    const fallback = `${type}/${id}`;
                    this.updateCache(ref, fallback);
                    return of(this.applyDisplayFormatting(ref, fallback, options));
                })
            );
        }

        const lowerType = (type || '').toLowerCase();
        const request$ = lowerType === 'patient'
            ? this.http.get<any>(`${this.backend}/Patient?_id=${id}`, {
                context: new HttpContext().set(LoadingUIEnabled, false)
            }).pipe(
                map((bundle) => bundle?.entry?.[0]?.resource || null)
            )
            : this.http.get<any>(`${this.backend}/${type}/${id}`, {
                context: new HttpContext().set(LoadingUIEnabled, false)
            });

        const sharedRequest$ = request$.pipe(
            map((res) => this.buildDisplayFromResource(type, res) || `${type}/${id}`),
            tap((display) => {
                this.updateCache(ref, display);
                this.inFlightRequests.delete(ref);
            }),
            catchError(() => {
                const fallback = `${type}/${id}`;
                this.updateCache(ref, fallback);
                this.inFlightRequests.delete(ref);
                return of(fallback);
            }),
            // Share the observable so late subscribers get the same result without re-triggering the HTTP call
            shareReplay(1)
        );

        this.inFlightRequests.set(ref, sharedRequest$);

        return sharedRequest$.pipe(
            map(display => this.applyDisplayFormatting(ref, display, options))
        );
    }

    private updateCache(ref: string, display: string) {
        this.cache[ref] = display;
        this.refDisplays$.next({ ...this.cache });
    }

    private applyDisplayFormatting(ref: string, display: string, options?: { instantReplace?: boolean; keepReference?: boolean }): string {
        if (!options?.instantReplace) {
            return display;
        }
        if (options.keepReference) {
            return `${ref}\n${display}`;
        }
        return display;
    }

    private splitRef(ref: string): { type: string | null; id: string | null } {
        // Supports "ResourceType/id" or full URLs ending in "ResourceType/id"
        try {
            const parts = ref.split('/');
            if (parts.length >= 2) {
                const id = parts[parts.length - 1] || null;
                const type = parts[parts.length - 2] || null;
                return { type, id };
            }
            return { type: null, id: null };
        } catch {
            return { type: null, id: null };
        }
    }

    private buildDisplayFromResource(type: string, res: any): string {
        if (!res) return '';
        const toHumanName = (r: any): string => {
            const name = r?.name;
            if (Array.isArray(name) && name.length) {
                const n = name[0] || {};
                if (n.text) return String(n.text);
                const given = Array.isArray(n.given) ? n.given.join(' ') : (n.given || '');
                const family = n.family || '';
                return `${(given || '').toString().trim()} ${(family || '').toString().trim()}`.trim();
            }
            if (typeof r?.name === 'string') return r.name;
            return r?.id || '';
        };

        switch ((type || '').toLowerCase()) {
            case 'patient':
            case 'practitioner':
            case 'relatedperson':
                return toHumanName(res);
            case 'organization':
                return res?.name || res?.id || '';
            case 'observation': {
                const cc = res?.code || {};
                if (cc.text) return String(cc.text);
                const coding = Array.isArray(cc.coding) ? cc.coding : [];
                return coding[0]?.display || coding[0]?.code || res?.id || '';
            }
            case 'medication':
            case 'medicationrequest': {
                const cc = res?.code || res?.medicationCodeableConcept || {};
                if (cc?.text) return String(cc.text);
                const coding = Array.isArray(cc?.coding) ? cc.coding : [];
                return coding[0]?.display || coding[0]?.code || res?.id || '';
            }
            case 'servicerequest': {
                const code = res?.code || {};
                const coding = Array.isArray(code?.coding) ? code.coding : [];
                return (
                    code?.text ||
                    coding[0]?.display ||
                    coding[0]?.code ||
                    res?.requisition?.value ||
                    res?.id ||
                    ''
                );
            }
            case 'location':
                return res?.name || res?.id || '';
                break;
            default:
                return res?.id || '';
        }
    }
}
